// client/src/components/plataforma/users/sections/PasswordSection.js
import React, { useState } from 'react';
import { Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';

const PasswordSection = ({ user, isCreating, isSelfService, onChange }) => {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [showCurrent, setShowCurrent] = useState(false);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');

    const handleCurrentPasswordChange = (e) => {
        const value = e.target.value;
        setCurrentPassword(value);
        if (onChange) {
            onChange('currentPassword', value);
        }
    };

    const handlePasswordChange = (e) => {
        const value = e.target.value;
        setPassword(value);
        if (onChange) {
            onChange('password', value);
        }
    };

    const handleConfirmChange = (e) => {
        setConfirmPassword(e.target.value);
    };

    const passwordsMatch = password === confirmPassword;
    const isPasswordValid = password.length >= 6 || password.length === 0;

    return (
        <div className="form-content">
            <div className="form-header">
                <h3 className="form-title">
                    <Lock size={20} className="inline mr-2" />
                    Segurança
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                    {isCreating 
                        ? 'Defina uma senha para o usuário (opcional)'
                        : 'Altere a senha do usuário'}
                </p>
            </div>

            <div className="space-y-6 max-w-md">
                {/* Senha Atual — obrigatória apenas quando o próprio usuário altera
                    a própria senha (isSelfService); um admin resetando a senha de
                    outro usuário não precisa saber a senha atual dele. */}
                {isSelfService && !isCreating && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Senha Atual
                        </label>
                        <div className="relative">
                            <Lock size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type={showCurrent ? "text" : "password"}
                                value={currentPassword}
                                onChange={handleCurrentPasswordChange}
                                className="form-input pl-10 pr-10"
                                placeholder="Digite sua senha atual"
                            />
                            <button
                                type="button"
                                onClick={() => setShowCurrent(!showCurrent)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>
                )}

                {/* Nova Senha */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {isCreating ? 'Senha (opcional)' : 'Nova Senha'}
                    </label>
                    <div className="relative">
                        <Lock size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={handlePasswordChange}
                            className="form-input pl-10 pr-10"
                            placeholder="Mínimo 6 caracteres"
                            minLength={6}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    </div>
                    {password && !isPasswordValid && (
                        <p className="text-xs text-red-600 mt-1">
                            A senha deve ter no mínimo 6 caracteres
                        </p>
                    )}
                </div>

                {/* Confirmar Senha */}
                {password && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Confirmar Senha
                        </label>
                        <div className="relative">
                            <Lock size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type={showConfirm ? "text" : "password"}
                                value={confirmPassword}
                                onChange={handleConfirmChange}
                                className="form-input pl-10 pr-10"
                                placeholder="Digite a senha novamente"
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirm(!showConfirm)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                        {confirmPassword && !passwordsMatch && (
                            <p className="text-xs text-red-600 mt-1">
                                As senhas não coincidem
                            </p>
                        )}
                    </div>
                )}

                {/* Aviso de segurança */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="text-blue-600 mt-0.5" size={20} />
                        <div>
                            <h4 className="text-sm font-medium text-blue-800">
                                {isCreating ? 'Sobre a senha' : 'Alteração de senha'}
                            </h4>
                            <p className="text-xs text-blue-700 mt-1">
                                {isCreating 
                                    ? 'Se não informar uma senha, o usuário precisará usar "Esqueci minha senha" no primeiro acesso.'
                                    : 'A senha deve ter no mínimo 6 caracteres. Após alterada, o usuário será notificado.'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PasswordSection;